import {
  HStack,
  VStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
  Checkbox,
  Box,
  Center,
} from '@chakra-ui/react';
import { Button, VisibleIf, Infobox, Input, TruncatableText } from 'Atoms';
import { Typography } from 'Tokens';
import { useCurrentCompany, useDebounce, useToast } from 'utils/hooks';
import {
  CompanyAssessment,
  ReportingGroup,
  useReportingGroupsQuery,
  BAssessment,
  CalculatedActivityResult,
  BusinessUnitCachedResult,
  useFlagCompanyAssessmentAsDirtyMutation,
  TaxonomyScore,
  Investor,
} from 'models';
import { ContentHeader, ContentLayout } from 'Molecules';
import { TaxonomyScoreChart } from 'Organisms';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { scoreSections } from 'utils/financials';
import { aggregateCachedResults } from 'utils/scores/taxonomyScore';
import {
  buildGroupTree,
  getAllBusinessUnitIds,
  isGroupCompletelySelected,
  isGroupPartiallySelected,
  NestedReportingGroup,
  useCompanyAssessmentResults,
} from './Results.hooks';
import { useTranslation } from 'utils/translation';
import { TaxonomyResultsTableContainer } from 'Features/TaxonomyResultsTable/TaxonomyResultsTableContainer';
import { useOnClickOutside } from 'usehooks-ts';
import { isEqual } from 'lodash';
import { AdjustIcon, RefreshIcon, SearchIcon } from 'Tokens/Icons/Function';
import { CompanyIcon } from 'Tokens/Icons/Data';
import { IdeaIcon } from 'Tokens/Icons/Status';
import { ChevronDownIcon, ChevronRightIcon } from 'Tokens/Icons/Direction';
import { TaxonomyExportMenu } from './TaxonomyExportMenu';
import { NegativeNumbersWarningBox } from 'Features/Financials/FinancialsTables/Header/NegativeNumbersWarningBox';
import { captureException } from '@sentry/react';
import {
  BusinessUnitsSortingType,
  businessUnitsSorter,
} from 'containers/Assessments/pieces/Assessment.hooks';
import { ReminderBox } from './ReminderBox';
import { FilteredBusinessUnits } from './FilteredBusinessUnits';

const CheckBoxItem = ({
  icon,
  title,
  isChecked,
  isDiabled,
  isIndeterminate,
  onClick,
  onChange,
}: {
  icon: JSX.Element;
  title: string;
  isChecked: boolean;
  isDiabled?: boolean;
  isIndeterminate?: boolean;
  onClick?: () => void;
  onChange: () => void;
}) => {
  return (
    <HStack
      justifyContent="space-between"
      w="100%"
      h="36px"
      p="8px"
      onClick={onClick ?? onChange}
      _hover={{
        bg: 'bg.hover',
      }}
    >
      <HStack>
        {icon}
        <TruncatableText w={'100%'} text={title ?? ''} />
      </HStack>
      <Checkbox
        isChecked={isChecked}
        isDisabled={isDiabled}
        isIndeterminate={isIndeterminate}
        onChange={onChange}
      />
    </HStack>
  );
};

const GroupMenu = ({
  group,
  checked,
  setChecked,
  isSubmenu,
}: {
  group: NestedReportingGroup;
  checked: string[];
  setChecked: Dispatch<SetStateAction<string[]>>;
  isSubmenu?: boolean;
}) => {
  const { t } = useTranslation('common');

  const { isOpen, onToggle } = useDisclosure();

  const allBUIds = getAllBusinessUnitIds(group);
  const isEmpty = allBUIds.length === 0;
  const isGroupSelected = isGroupCompletelySelected(group, checked);
  const isIndeterminate = isGroupPartiallySelected(group, checked);

  const onGroupClick = (group: NestedReportingGroup) => {
    const groupBUIds = getAllBusinessUnitIds(group);
    setChecked((prev) => {
      if (isGroupSelected) {
        return prev.filter((id) => !groupBUIds.includes(id));
      } else {
        const newIds = groupBUIds.filter((id) => !prev.includes(id));
        return [...prev, ...newIds];
      }
    });
  };

  const onBuClick = (id: string) => {
    setChecked((prev) => {
      if (prev.includes(id)) {
        return prev.filter((i) => i !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  return (
    <VStack p="0px" pl={isSubmenu ? '32px' : '0px'} m="0px" w="100%">
      <CheckBoxItem
        icon={isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
        title={group.name}
        isChecked={isGroupSelected}
        isDiabled={isEmpty}
        isIndeterminate={isIndeterminate}
        onClick={onToggle}
        onChange={() => onGroupClick(group)}
      />
      <Box p="0px" m="0px" w="100%" height={isOpen ? 'auto' : '0px'} overflow="hidden">
        {group.children?.map((child) => (
          <GroupMenu
            key={child.id}
            group={child}
            checked={checked}
            setChecked={setChecked}
            isSubmenu
          />
        ))}
        {group.businessUnits.map((bu) => (
          <Box p="0px" pl="32px" m="0px">
            <CheckBoxItem
              key={bu?.businessUnit?.id}
              title={bu?.businessUnit?.name ?? ''}
              isChecked={checked.includes(bu?.businessUnit?.id)}
              icon={<CompanyIcon />}
              onChange={() => onBuClick(bu?.businessUnit?.id)}
            />
          </Box>
        ))}

        <Center>
          {!group.businessUnits.length && !group.children?.length && (
            <VStack
              alignContent="center"
              paddingX="28px"
              paddingY="20px"
              width="202px"
              textAlign="center"
            >
              <Typography variant="bodyStrong">{t('common:words.empty')}</Typography>
              <Typography variant="detail">{group.name}</Typography>
              <Typography variant="detail">{t('common:results.reportingUnits')}</Typography>
            </VStack>
          )}
        </Center>
      </Box>
    </VStack>
  );
};

const FilterSelector = ({
  selected,
  onConfirm,
  groups,
  orphans,
}: {
  selected: string[];
  onConfirm: (vals: string[]) => void;
  groups: ReportingGroup[];
  orphans: BAssessment[];
}) => {
  const { t } = useTranslation('common');
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [checked, setChecked] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const searchValue = useDebounce(search);

  const ref = useRef<HTMLElement>(null);

  useOnClickOutside<HTMLElement>(ref as React.RefObject<HTMLElement>, onClose);

  const groupsTree = buildGroupTree(groups, searchValue);

  const filteredOrphans = orphans.filter((o) =>
    o.businessUnit?.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleCheckbox = (id: string) => {
    setChecked((prev) => {
      if (prev.includes(id)) {
        return prev.filter((i) => i !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  useEffect(() => {
    setChecked(selected);
  }, [selected]);

  return (
    <Popover closeOnBlur={true} placement="left" isOpen={isOpen} id="filter-popover">
      <PopoverTrigger>
        <Button
          leftIcon={<AdjustIcon color="inherit" />}
          variant="secondary"
          size="md"
          onClick={onToggle}
        >
          {t('common:words.filter')}
        </Button>
      </PopoverTrigger>
      <PopoverContent as={VStack} spacing="8px" minWidth="300px" padding="12px 8px" ref={ref}>
        <VStack w="100%" spacing="4px">
          <HStack width="100%" justifyContent="space-between">
            <Typography variant="h4">{t('common:results.reportFor')} </Typography>
            <Button variant="ghost" size="sm" onClick={() => setChecked([])}>
              {t('common:button.clearAll')}
            </Button>
          </HStack>
          <Input
            w="100%"
            size="md"
            leftIcon={<SearchIcon />}
            placeholder={'Search'}
            onChange={(e) => setSearch(e.target.value)}
          />
        </VStack>
        <Box width="100%" maxHeight="350px" p="0" m="0" overflow="auto">
          {groupsTree.map((group) => (
            <GroupMenu key={group.id} group={group} checked={checked} setChecked={setChecked} />
          ))}
          {filteredOrphans.map((bu) => (
            <CheckBoxItem
              key={bu?.businessUnit?.id}
              title={bu?.businessUnit?.name ?? ''}
              isChecked={checked.includes(bu?.businessUnit?.id)}
              icon={<CompanyIcon />}
              onChange={() => handleCheckbox(bu?.businessUnit?.id)}
            />
          ))}
        </Box>
        <Button
          variant="primary"
          width="full"
          size="md"
          isDisabled={isEqual(checked, selected)}
          onClick={() => {
            onConfirm(checked);
            onClose();
          }}
        >
          {t('common:button.confirm')}
        </Button>
      </PopoverContent>
    </Popover>
  );
};

const EMPTY_SCORE: CalculatedActivityResult['score'] = scoreSections.reduce(
  (agg, section) => {
    agg[section] = {
      total: 100,
      eligible: 0,
      aligned: 0,
      inProgress: 100,
    };
    return agg;
  },
  {} as { [key: string]: TaxonomyScore }
) as CalculatedActivityResult['score'];

const ScoreTotal = ({ results }: { results: BusinessUnitCachedResult[] }) => {
  const { score, progress } = useMemo(() => {
    return aggregateCachedResults(results);
  }, [results]);

  return (
    <HStack
      justifyContent="center"
      width="100%"
      spacing="16px"
      borderWidth="1px"
      borderColor="border.decorative"
      borderRadius="8px"
      padding="16px"
    >
      {scoreSections.map((section) => {
        return (
          <TaxonomyScoreChart
            title={section}
            size="lg"
            progress={progress}
            score={score[section]}
            key={section}
            withDetails={true}
          />
        );
      })}
    </HStack>
  );
};

export const Results = ({
  cAssessment,
  isGroup = false,
  businessUnitsSorting,
  investors,
}: {
  cAssessment: CompanyAssessment;
  isGroup?: boolean;
  businessUnitsSorting: BusinessUnitsSortingType;
  investors: Investor[];
}) => {
  const { t } = useTranslation('common');
  const { company } = useCurrentCompany();
  const [searchParams, setSearchParams] = useSearchParams();
  const [flagAssessment] = useFlagCompanyAssessmentAsDirtyMutation();
  const toast = useToast();
  const { sortAlphabetically } = businessUnitsSorting;

  const { data: reportingGroupsData } = useReportingGroupsQuery({
    variables: {
      cAssessmentId: cAssessment?.id,
    },
    skip: !cAssessment?.id,
  });

  const reportingGroups = reportingGroupsData?.reportingGroups ?? [];

  const { data: cAssessmentResults, loading } = useCompanyAssessmentResults(
    cAssessment?.id ?? '',
    false,
    isGroup
  );

  const allBusinessUnits =
    cAssessmentResults?.businessUnitResults.map((bu) => {
      const bAssessment = cAssessment?.bAssessments.find(
        (b) => b?.businessUnit?.id === bu?.businessUnit?.id
      );

      return {
        ...bAssessment,
        results: bu,
        group: reportingGroups.find((g) =>
          g.businessUnits.find((businessUnit) => businessUnit.bAssessmentId === bAssessment?.id)
        ),
      };
    }) ?? [];
  const { selectedBusinessUnits, selectedIds } = useMemo(() => {
    const selectedBUs = searchParams.get('businessUnits')?.split('_') ?? [];
    const businessUnits = cAssessment?.bAssessments
      .filter((b) => selectedBUs?.includes(b.businessUnit?.id))
      .map((bA) => ({
        ...bA,
        group: reportingGroups.find((g) =>
          g.businessUnits.find((bu) => bu.bAssessmentId === bA.id)
        ),
        results: cAssessmentResults?.businessUnitResults.find((buR) => buR.bAssessmentId === bA.id),
      }));
    return { selectedBusinessUnits: businessUnits ?? [], selectedIds: selectedBUs ?? [] };
  }, [searchParams, cAssessmentResults]);

  const tempBusinessUnits = selectedBusinessUnits.length ? selectedBusinessUnits : allBusinessUnits;
  const businessUnits = sortAlphabetically
    ? tempBusinessUnits.sort((a, b) =>
        businessUnitsSorter(a.businessUnit?.name ?? '', b.businessUnit?.name ?? '')
      )
    : tempBusinessUnits;

  const filteredBusinessUnits = cAssessment?.bAssessments.filter((b) => !b?.reportingGroup?.id);
  const filterSelectorBusinessUnits = sortAlphabetically
    ? filteredBusinessUnits?.sort((a, b) =>
        businessUnitsSorter(a.businessUnit?.name ?? '', b.businessUnit?.name ?? '')
      )
    : filteredBusinessUnits;

  const selectedBusinessUnitIds = useMemo(() => {
    return selectedIds.filter((id) => id);
  }, [selectedBusinessUnits]);

  const onSelectionChange = (businessUnitIds: string[]) => {
    setSearchParams({ businessUnits: businessUnitIds.length ? businessUnitIds.join('_') : '' });
  };

  return (
    <ContentLayout
      header={<ContentHeader size="md" title={t('common:taxonomyScore')} />}
      isLoading={loading}
      noHeader
      loadingText={t('common:calculating')}
      variant="inline.nopad"
      paddingTop="24px"
      paddingBottom="56px"
    >
      <VStack width="100%" alignItems="stretch" spacing="32px">
        <VStack width="100%" alignItems="stretch" spacing="12px">
          <Infobox
            paddingRight="24px"
            closable={false}
            status="info"
            icon={<IdeaIcon color="text.info" width="20px" />}
            title={t('common:results.infobox.title')}
            description={t('common:results.infobox.description')}
            containerStyles={{
              alignItems: 'center',
            }}
            rightElement={
              <VisibleIf condition={!isGroup}>
                <Button
                  variant="secondary"
                  leftIcon={<RefreshIcon color="inherit" />}
                  onClick={() => {
                    flagAssessment({
                      variables: { cachedResultId: cAssessment?.cachedResultId },
                    }).catch((e) => {
                      captureException(e, {
                        extra: { errorMessage: t('common:results.toast.error') },
                      });
                      toast({
                        text: t('common:results.toast.error'),
                        variant: 'danger',
                      });
                    });
                  }}
                >
                  {t('common:results.refresh')}
                </Button>
              </VisibleIf>
            }
          />
          <ReminderBox investors={investors} company={company} />
        </VStack>
        <HStack justifyContent="space-between" alignItems="flex-start" width="100%">
          <HStack spacing="8px">
            <Typography variant="body">{t('common:reportFor')} </Typography>
            {selectedBusinessUnits.length ? (
              <FilteredBusinessUnits
                selectedBusinessUnits={selectedBusinessUnits}
                onSelectionChange={onSelectionChange}
                maxVisibleTags={3}
              />
            ) : (
              <HStack>
                <CompanyIcon boxSize="13.3px" />
                <Typography variant="bodyStrong">{company?.name}</Typography>
              </HStack>
            )}
          </HStack>
          <HStack spacing="8px" flexShrink={0}>
            <VisibleIf condition={!isGroup}>
              <FilterSelector
                selected={selectedIds}
                groups={reportingGroups}
                orphans={filterSelectorBusinessUnits ?? []}
                onConfirm={(values) => onSelectionChange(values)}
              />
            </VisibleIf>
            <TaxonomyExportMenu
              company={company}
              cAssessmentResults={cAssessmentResults}
              assessment={cAssessment}
              isGroup={isGroup}
            />
          </HStack>
        </HStack>
        <NegativeNumbersWarningBox />
        {selectedBusinessUnits && cAssessmentResults?.cachedResult && (
          <ScoreTotal
            results={
              selectedBusinessUnits?.length
                ? (selectedBusinessUnits
                    ?.map((bu) => bu?.results?.cachedResult)
                    .filter(Boolean) as BusinessUnitCachedResult[])
                : ([cAssessmentResults?.cachedResult] as BusinessUnitCachedResult[])
            }
          />
        )}
        <TaxonomyResultsTableContainer
          selectedBusinessUnitIds={selectedBusinessUnitIds}
          cAssessmentResults={cAssessmentResults}
        />
        {!isGroup && (
          <VStack spacing="16px" width="100%" alignItems="stretch">
            <Typography variant="h2">{t('common:results.breakdown')}</Typography>
            {businessUnits.map((bu) => (
              <VStack
                key={`${bu.id}-results`}
                padding="16px"
                spacing="16px"
                borderRadius="8px"
                borderWidth="1px"
                borderColor="border.decorative"
              >
                <VStack alignItems="flex-start" width="100%" spacing="4px">
                  <Typography variant="h3">{bu?.businessUnit?.name}</Typography>
                  <Typography variant="body">{bu.group?.name}</Typography>
                </VStack>
                <HStack justifyContent="center" width="100%" spacing="16px">
                  {scoreSections.map((section) => {
                    const score = bu.results?.cachedResult?.score ?? EMPTY_SCORE;
                    const progress = bu.results?.cachedResult?.progress;
                    return (
                      <TaxonomyScoreChart
                        progress={progress}
                        title={section}
                        size="lg"
                        score={{
                          total: 100,
                          ...score[section],
                        }}
                        key={`${section}-${bu.id}`}
                        withDetails={true}
                        businessUnitId={bu.id}
                      />
                    );
                  })}
                </HStack>
              </VStack>
            ))}
          </VStack>
        )}
      </VStack>
    </ContentLayout>
  );
};
